import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LeadScoreRing from './LeadScoreRing';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  AlertTriangle, Clock, Calendar, ArrowRight, Loader2,
  Phone, Mail, MessageSquare, Sparkles, Search, Flame, Snowflake, Thermometer,
  ChevronRight, Target, Users, CheckCircle2,
} from 'lucide-react';
import { cn } from '../lib/utils';

const STAGE_LABELS = {
  new_lead: 'New Lead', contacted: 'Contacted', discovery: 'Discovery',
  educating: 'Educating', pre_qualifying: 'Pre-Qualifying',
  ready_to_proceed: 'Ready to Proceed', long_term_nurture: 'Long-term Nurture',
  lost: 'Lost', converted: 'Converted',
};
const STAGE_DOT = {
  new_lead: 'bg-purple-500', contacted: 'bg-blue-500', discovery: 'bg-cyan-500',
  educating: 'bg-teal-500', pre_qualifying: 'bg-amber-500',
  ready_to_proceed: 'bg-orange-500', long_term_nurture: 'bg-indigo-500',
  lost: 'bg-slate-400', converted: 'bg-emerald-500',
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (Math.abs(diff) < 60) return 'just now';
  const abs = Math.abs(diff);
  let label;
  if (abs < 3600) label = `${Math.floor(abs / 60)}m`;
  else if (abs < 86400) label = `${Math.floor(abs / 3600)}h`;
  else label = `${Math.floor(abs / 86400)}d`;
  return diff > 0 ? `${label} ago` : `in ${label}`;
};

// ---------------------------------------------------------------------------
// Action card — styled like Read My Income's stat cards
// ---------------------------------------------------------------------------

const ActionCard = ({ tone, label, count, sublabel, icon: Icon, onClick, items = [] }) => {
  const cfg = {
    rose: {
      ring: 'border-rose-300/60 dark:border-rose-500/30',
      iconBg: 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-300',
      digit: 'text-rose-600 dark:text-rose-300',
      labelClr: 'text-rose-700 dark:text-rose-300',
      hover: 'hover:border-rose-400 dark:hover:border-rose-500/60',
    },
    amber: {
      ring: 'border-amber-300/60 dark:border-amber-500/30',
      iconBg: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300',
      digit: 'text-amber-700 dark:text-amber-300',
      labelClr: 'text-amber-700 dark:text-amber-300',
      hover: 'hover:border-amber-400 dark:hover:border-amber-500/60',
    },
    sky: {
      ring: 'border-sky-300/60 dark:border-sky-500/30',
      iconBg: 'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-300',
      digit: 'text-sky-700 dark:text-sky-300',
      labelClr: 'text-sky-700 dark:text-sky-300',
      hover: 'hover:border-sky-400 dark:hover:border-sky-500/60',
    },
  }[tone];

  return (
    <button
      onClick={onClick}
      className={cn(
        'group text-left rounded-xl border-2 bg-white dark:bg-slate-900/40 p-5 transition-all hover:-translate-y-0.5 active:scale-[0.99]',
        cfg.ring, cfg.hover,
      )}
      data-testid={`triage-action-${tone}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', cfg.iconBg)}>
          <Icon className="w-5 h-5" />
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition" />
      </div>
      <div className={cn('text-xs uppercase tracking-wide font-semibold mb-1', cfg.labelClr)}>{label}</div>
      <div className={cn('text-4xl font-bold tabular-nums tracking-tight', cfg.digit)}>{count}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sublabel}</div>

      {items.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-1">
          {items.slice(0, 2).map((it, i) => (
            <li key={i} className="text-xs text-slate-600 dark:text-slate-400 truncate flex items-center gap-1.5">
              <span className={cn('w-1 h-1 rounded-full flex-shrink-0', STAGE_DOT[it.lead?.status] || 'bg-slate-400')} />
              <span className="truncate">{it.lead?.name || 'Unknown'}</span>
              <span className="ml-auto opacity-60 flex-shrink-0">{it.method}</span>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Lead row — styled like Read My Income's lender cards
// ---------------------------------------------------------------------------

const LeadRow = ({ lead, onOpen, onCall, onEmail, onSMS }) => (
  <div
    className="group flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-lg cursor-pointer transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/40"
    onClick={onOpen}
    data-testid={`triage-lead-row-${lead.id}`}
  >
    <LeadScoreRing
      score={lead.lead_score || 0}
      name={lead.name}
      temperature={lead.lead_temperature || 'Cold'}
      size={44}
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <h4 className="font-semibold text-slate-900 dark:text-slate-100 truncate text-sm">
          {lead.name || 'Unknown'}
        </h4>
        {lead.lead_temperature === 'Hot' && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 text-[10px] font-bold uppercase tracking-wider">
            <Flame className="w-2.5 h-2.5" /> Hot
          </span>
        )}
      </div>
      <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className={cn('w-1.5 h-1.5 rounded-full', STAGE_DOT[lead.status] || 'bg-slate-400')} />
          {STAGE_LABELS[lead.status] || lead.status || '—'}
        </span>
        {lead.timeframe && <span className="opacity-80">⏱ {lead.timeframe}</span>}
        {lead.lead_type && <span className="opacity-80">· {lead.lead_type}</span>}
        {lead.last_contacted_at && <span className="opacity-60">last contact {fmtRelative(lead.last_contacted_at)}</span>}
      </div>
    </div>
    {/* Actions — always visible on touch, hover on desktop */}
    <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
      {lead.phone && (
        <button
          onClick={(e) => { e.stopPropagation(); onCall?.(lead); }}
          className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 active:scale-95 transition-all"
          title="Call"
          data-testid={`triage-call-${lead.id}`}
        >
          <Phone className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      )}
      {lead.email && (
        <button
          onClick={(e) => { e.stopPropagation(); onEmail?.(lead); }}
          className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 active:scale-95 transition-all"
          title="Email"
          data-testid={`triage-email-${lead.id}`}
        >
          <Mail className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      )}
      {lead.phone && (
        <button
          onClick={(e) => { e.stopPropagation(); onSMS?.(lead); }}
          className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-500/10 active:scale-95 transition-all"
          title="SMS"
          data-testid={`triage-sms-${lead.id}`}
        >
          <MessageSquare className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      )}
    </div>
  </div>
);

// Temp/funnel bar
const FunnelBar = ({ label, count, total, color }) => {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
        <span className={cn('w-1.5 h-1.5 rounded-full', color)} />
        {label}
      </div>
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-7 text-right text-xs font-semibold text-slate-700 dark:text-slate-300">{count}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TriageHome — site-style (matches Read My Income / Home palette)
// ---------------------------------------------------------------------------

const TriageHome = ({ leads = [], followups = null, loading = false, onOpenLead, onLogAction, onAddLead }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const highIntent = useMemo(() => {
    return leads
      .filter((l) => !['lost', 'converted'].includes(l.status))
      .filter((l) => !search || (l.name || '').toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0))
      .slice(0, 8);
  }, [leads, search]);

  const pipelineCounts = useMemo(() => {
    const out = {};
    leads.forEach((l) => { out[l.status] = (out[l.status] || 0) + 1; });
    return out;
  }, [leads]);

  const tempCounts = useMemo(() => {
    const c = { Hot: 0, Warm: 0, Cold: 0 };
    leads.forEach((l) => { c[l.lead_temperature || 'Cold'] = (c[l.lead_temperature || 'Cold'] || 0) + 1; });
    return c;
  }, [leads]);

  const counts = followups?.counts || { overdue: 0, due_today: 0, due_this_week: 0 };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="lead-triage-home">

      {/* Hero pill + title */}
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
          <Sparkles className="h-3 w-3" /> Daily Triage · Lead Nurture
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="space-y-2 min-w-0">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              {getGreeting()},{' '}
              <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 dark:from-emerald-300 dark:via-teal-300 dark:to-emerald-400 bg-clip-text text-transparent">
                {(user?.name || 'Broker').split(' ')[0]}
              </span>
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base max-w-2xl">
              {counts.overdue > 0
                ? <><span className="text-rose-600 dark:text-rose-400 font-semibold">{counts.overdue} overdue</span> · {counts.due_today} due today · {counts.due_this_week} this week</>
                : counts.due_today > 0
                  ? <>{counts.due_today} follow-ups due today · {counts.due_this_week} this week</>
                  : <>You're caught up. Time to warm up some leads.</>}
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads…"
              className="pl-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 h-11"
              data-testid="triage-search"
            />
          </div>
        </div>
      </div>

      {/* Action cards — Overdue / Today / This Week */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <ActionCard
          tone="rose" label="Overdue" icon={AlertTriangle}
          count={counts.overdue} sublabel={counts.overdue > 0 ? 'Action now' : 'Nothing slipped'}
          items={followups?.overdue || []}
          onClick={() => navigate('/lead-nurture')}
        />
        <ActionCard
          tone="amber" label="Due Today" icon={Clock}
          count={counts.due_today} sublabel="On your plate today"
          items={followups?.due_today || []}
          onClick={() => navigate('/lead-nurture')}
        />
        <ActionCard
          tone="sky" label="This Week" icon={Calendar}
          count={counts.due_this_week} sublabel="Plan ahead"
          items={followups?.due_this_week || []}
          onClick={() => navigate('/lead-nurture')}
        />
      </div>

      {/* Main split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* High-intent leads */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Flame className="h-5 w-5 text-orange-500" />
                High-Intent Leads
                <Badge variant="outline" className="ml-1 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                  Top {highIntent.length}
                </Badge>
              </CardTitle>
              <Button
                variant="outline" size="sm"
                onClick={onAddLead}
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
                data-testid="triage-add-lead-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> New lead
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads…
              </div>
            ) : highIntent.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Sparkles className="w-12 h-12 text-emerald-500 mb-3" />
                <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">No leads yet</p>
                <p className="text-sm text-slate-500 mt-1">Add your first lead to start the journey.</p>
                <Button onClick={onAddLead} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white" size="sm">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Add lead
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                {highIntent.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    onOpen={() => onOpenLead?.(lead)}
                    onCall={(l) => onLogAction?.('Call', l)}
                    onEmail={(l) => onLogAction?.('Email', l)}
                    onSMS={(l) => onLogAction?.('SMS', l)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column: Temperature + Pipeline */}
        <div className="space-y-4">
          <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Thermometer className="h-4 w-4 text-amber-500" />
                Lead Temperature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                <TempBlock count={tempCounts.Hot || 0} label="Hot" Icon={Flame} bg="bg-orange-50 dark:bg-orange-500/10" text="text-orange-600 dark:text-orange-400" />
                <TempBlock count={tempCounts.Warm || 0} label="Warm" Icon={Thermometer} bg="bg-amber-50 dark:bg-amber-500/10" text="text-amber-600 dark:text-amber-400" />
                <TempBlock count={tempCounts.Cold || 0} label="Cold" Icon={Snowflake} bg="bg-blue-50 dark:bg-blue-500/10" text="text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Target className="h-4 w-4 text-emerald-500" />
                  Pipeline
                </CardTitle>
                <span className="text-xs text-slate-500">{leads.length} total</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <FunnelBar label="New" count={pipelineCounts.new_lead || 0} total={leads.length} color={STAGE_DOT.new_lead} />
              <FunnelBar label="Contacted" count={pipelineCounts.contacted || 0} total={leads.length} color={STAGE_DOT.contacted} />
              <FunnelBar label="Discovery" count={pipelineCounts.discovery || 0} total={leads.length} color={STAGE_DOT.discovery} />
              <FunnelBar label="Educating" count={pipelineCounts.educating || 0} total={leads.length} color={STAGE_DOT.educating} />
              <FunnelBar label="Pre-Qual" count={pipelineCounts.pre_qualifying || 0} total={leads.length} color={STAGE_DOT.pre_qualifying} />
              <FunnelBar label="Ready" count={pipelineCounts.ready_to_proceed || 0} total={leads.length} color={STAGE_DOT.ready_to_proceed} />
              <FunnelBar label="Long-term" count={pipelineCounts.long_term_nurture || 0} total={leads.length} color={STAGE_DOT.long_term_nurture} />
            </CardContent>
          </Card>

          <button
            onClick={() => navigate('/lead-nurture')}
            className="group w-full flex items-center justify-between gap-3 px-5 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.99] hover:shadow-emerald-500/30"
            data-testid="triage-go-to-followups"
          >
            <div className="text-left">
              <div className="text-xs uppercase tracking-wider opacity-80">Daily Action</div>
              <div className="font-bold">Open Follow-ups</div>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

const TempBlock = ({ count, label, Icon, bg, text }) => (
  <div className={cn('rounded-lg p-3 flex flex-col items-start gap-1', bg)}>
    <Icon className={cn('w-4 h-4', text)} />
    <div className={cn('text-2xl font-bold tabular-nums', text)}>{count}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
  </div>
);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export default TriageHome;
